/**
 * Winner Bundle Component
 *
 * 固定 3 个赢家产品的精简 bundle。
 * 交互：勾选/取消、换色（联动图片与变体下拉）、变体切换、数量角标、实时算价、整套加购。
 * 折扣规则：勾选的不同产品 >= 2 时启用额外折扣（纯展示，真实折扣由 Shopify 后台自动折扣兜底）。
 */
(function () {
  'use strict';

  if (customElements.get('winner-bundle')) return;

  const MAX_QTY = 9;

  class WinnerBundle extends HTMLElement {
    connectedCallback() {
      this.config = {
        discountPercentage: parseFloat(this.dataset.discountPercentage) || 0,
        moneyFormat: this.dataset.moneyFormat || '${{amount}}',
        currencyCode: this.dataset.currencyCode || '',
        countryCode: this.dataset.countryCode || '',
        texts: {
          item: this.dataset.textItem || 'item',
          items: this.dataset.textItems || 'items',
          adding: this.dataset.textAdding || 'Adding...',
          added: this.dataset.textAdded || 'Bundle added to cart!',
          addFailed: this.dataset.textAddFailed || 'Failed to add bundle',
          saveBadge: this.dataset.textSaveBadge || 'Save [[p]]%'
        }
      };

      this.rows = [];
      this.querySelectorAll('[data-wb-row]').forEach((rowEl) => {
        const row = this.initRow(rowEl);
        if (row) this.rows.push(row);
      });

      this.summaryEls = {
        count: this.querySelector('[data-wb-count]'),
        countLabel: this.querySelector('[data-wb-count-label]'),
        subtotal: this.querySelector('[data-wb-subtotal]'),
        subtotalCompare: this.querySelector('[data-wb-subtotal-compare]'),
        discountRow: this.querySelector('[data-wb-discount-row]'),
        discountAmount: this.querySelector('[data-wb-discount-amount]'),
        discountHint: this.querySelector('[data-wb-discount-hint]'),
        saveBadge: this.querySelector('[data-wb-save-badge]'),
        total: this.querySelector('[data-wb-total]'),
        compareTotal: this.querySelector('[data-wb-compare-total]'),
        addToCart: this.querySelector('[data-wb-add-to-cart]')
      };

      if (this.summaryEls.addToCart) {
        this.summaryEls.addToCart.addEventListener('click', () => this.handleAddToCart());
      }

      // 点击组件外部收起数量胶囊
      this.onDocClick = (e) => {
        if (!e.target.closest('[data-wb-qty-popover], [data-wb-qty-badge]')) {
          this.closeAllQtyPopovers();
        }
      };
      document.addEventListener('click', this.onDocClick);

      // 与页面主变体选择器双向同步（仅当前页面产品行）
      this.onPageVariantChange = (e) => {
        const variantId = e.detail && e.detail.variant && e.detail.variant.id;
        if (!variantId) return;
        const row = this.rows.find((r) => r.isCurrentProduct);
        if (!row || String(row.variant.id) === String(variantId)) return;
        const variant = row.data.variants.find((v) => String(v.id) === String(variantId));
        if (variant) this.applyExternalVariant(row, variant);
      };
      document.addEventListener('product:variant-change', this.onPageVariantChange);

      this.updateSummary();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('product:variant-change', this.onPageVariantChange);
    }

    /* ---------- 行初始化 ---------- */

    initRow(rowEl) {
      const jsonEl = rowEl.querySelector('[data-wb-product-json]');
      if (!jsonEl) return null;

      let data;
      try {
        data = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('[Winner Bundle] Invalid product JSON', e);
        return null;
      }

      const row = {
        el: rowEl,
        data: data,
        locked: rowEl.dataset.locked === 'true',
        isCurrentProduct: rowEl.dataset.isCurrentProduct === 'true',
        checked: rowEl.dataset.checked === 'true',
        qty: 1,
        variant: data.variants.find((v) => String(v.id) === rowEl.dataset.variantId) || data.variants[0],
        els: {
          check: rowEl.querySelector('[data-wb-check]'),
          image: rowEl.querySelector('[data-wb-image]'),
          colorName: rowEl.querySelector('[data-wb-color-name]'),
          select: rowEl.querySelector('[data-wb-select]'),
          priceCurrent: rowEl.querySelector('[data-wb-price-current]'),
          priceCompare: rowEl.querySelector('[data-wb-price-compare]'),
          qtyBadge: rowEl.querySelector('[data-wb-qty-badge]'),
          qtyBadgeValue: rowEl.querySelector('[data-wb-qty-value]'),
          qtyPopover: rowEl.querySelector('[data-wb-qty-popover]'),
          qtyNum: rowEl.querySelector('[data-wb-qty-num]'),
          qtyDec: rowEl.querySelector('[data-wb-qty-dec]'),
          qtyInc: rowEl.querySelector('[data-wb-qty-inc]')
        }
      };

      // 勾选（checkbox 或产品图片均可触发）
      if (row.els.check) {
        row.els.check.addEventListener('click', () => this.toggleRow(row));
      }
      const imageWrap = rowEl.querySelector('.wb-media__image');
      if (imageWrap) {
        imageWrap.addEventListener('click', () => this.toggleRow(row));
      }

      // 色块
      rowEl.querySelectorAll('[data-wb-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => {
          if (swatch.dataset.available !== 'true') return;
          this.pickColor(row, swatch);
        });
      });

      // 变体下拉
      if (row.els.select) {
        row.els.select.addEventListener('change', () => {
          const variant = row.data.variants.find((v) => String(v.id) === row.els.select.value);
          if (variant) this.setVariant(row, variant);
        });
      }

      // 数量角标 / 胶囊
      if (row.els.qtyBadge) {
        row.els.qtyBadge.addEventListener('click', (e) => {
          e.stopPropagation();
          if (!row.checked) {
            this.toggleRow(row);
            return;
          }
          this.openQtyPopover(row);
        });
      }
      if (row.els.qtyPopover) {
        row.els.qtyPopover.addEventListener('click', (e) => e.stopPropagation());
        row.els.qtyDec.addEventListener('click', () => this.setQty(row, row.qty - 1));
        row.els.qtyInc.addEventListener('click', () => this.setQty(row, row.qty + 1));
        rowEl.querySelector('[data-wb-qty-ok]').addEventListener('click', () => this.closeQtyPopover(row));
      }

      return row;
    }

    /* ---------- 勾选 ---------- */

    toggleRow(row) {
      if (row.locked) return;
      row.checked = !row.checked;
      row.el.dataset.checked = String(row.checked);
      row.el.classList.toggle('wb-row--unchecked', !row.checked);
      row.els.check.setAttribute('aria-checked', String(row.checked));
      this.updateSummary();
    }

    /* ---------- 换色 ---------- */

    pickColor(row, swatchEl) {
      const colorValue = swatchEl.dataset.value;
      const colorIndex = row.data.colorIndex;
      if (colorIndex < 0) return;

      // 更新选中环 + 色名
      row.el.querySelectorAll('[data-wb-swatch]').forEach((s) => {
        const active = s === swatchEl;
        s.classList.toggle('wb-swatch--active', active);
        s.setAttribute('aria-pressed', String(active));
      });
      if (row.els.colorName) {
        row.els.colorName.textContent = swatchEl.dataset.displayValue || colorValue;
      }

      // 该颜色下的候选变体
      const candidates = row.data.variants.filter((v) => v.options[colorIndex] === colorValue);
      if (!candidates.length) return;

      // 优先保留当前非颜色选项组合
      let next = candidates.find((v) =>
        v.options.every((opt, i) => i === colorIndex || opt === row.variant.options[i])
      );
      if (!next || !next.available) {
        next = candidates.find((v) => v.available) || candidates[0];
      }

      // 重建变体下拉（只列该颜色的组合，文案不带颜色）
      this.buildSelect(row, colorValue, next.id);

      this.setVariant(row, next);
    }

    buildSelect(row, colorValue, selectedId) {
      if (!row.els.select) return;
      const colorIndex = row.data.colorIndex;
      const candidates = colorIndex >= 0
        ? row.data.variants.filter((v) => v.options[colorIndex] === colorValue)
        : row.data.variants;
      row.els.select.innerHTML = candidates
        .map((v) => {
          const label = colorIndex >= 0
            ? v.options.filter((_, i) => i !== colorIndex).join(' / ')
            : v.options.join(' / ');
          const sel = v.id === selectedId ? ' selected' : '';
          const dis = v.available ? '' : ' disabled';
          return `<option value="${v.id}"${sel}${dis}>${this.escapeHtml(label)}</option>`;
        })
        .join('');
    }

    /* ---------- 与页面主变体选择器双向同步 ---------- */

    // 页面 → 组件：外部变体变化时刷新当前产品行
    applyExternalVariant(row, variant) {
      const colorIndex = row.data.colorIndex;
      if (colorIndex >= 0) {
        const colorValue = variant.options[colorIndex];
        const swatchEl = Array.from(row.el.querySelectorAll('[data-wb-swatch]'))
          .find((s) => s.dataset.value === colorValue);
        if (swatchEl) {
          row.el.querySelectorAll('[data-wb-swatch]').forEach((s) => {
            const active = s === swatchEl;
            s.classList.toggle('wb-swatch--active', active);
            s.setAttribute('aria-pressed', String(active));
          });
          if (row.els.colorName) {
            row.els.colorName.textContent = swatchEl.dataset.displayValue || colorValue;
          }
        }
        this.buildSelect(row, colorValue, variant.id);
      } else if (row.els.select) {
        row.els.select.value = String(variant.id);
      }

      this._syncingFromPage = true;
      this.setVariant(row, variant);
      this._syncingFromPage = false;
    }

    // 组件 → 页面：把变体写回主题隐藏 radio，触发主题联动管线
    pushVariantToPage(row, variant) {
      const vs = document.querySelector('variant-selects:not([data-sticky])');
      if (!vs) return;

      let lastRadio = null;
      variant.options.forEach((value, i) => {
        const optionName = row.data.options[i];
        if (!optionName) return;
        const handle = optionName.toLowerCase().replace(/\s+/g, '-');
        const fieldset = vs.querySelector(`fieldset[data-handle="${handle}"]`);
        if (!fieldset) return;
        fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
          if (radio.value === value && !radio.checked) {
            radio.checked = true;
            lastRadio = radio;
          }
        });
      });

      if (lastRadio) {
        lastRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ---------- 变体切换 ---------- */

    setVariant(row, variant) {
      row.variant = variant;
      row.el.dataset.variantId = String(variant.id);

      if (row.els.image && variant.image) {
        row.els.image.src = variant.image;
      }

      this.updateRowPrice(row);
      this.updateSummary();

      // 当前产品行的选择回写页面主选择器（来自页面的同步不回写，防循环）
      if (row.isCurrentProduct && !this._syncingFromPage) {
        this.pushVariantToPage(row, variant);
      }
    }

    /* ---------- 数量 ---------- */

    openQtyPopover(row) {
      this.closeAllQtyPopovers();
      if (row.els.qtyPopover) {
        row.els.qtyPopover.hidden = false;
        row.els.qtyBadge.hidden = true;
      }
    }

    closeQtyPopover(row) {
      if (row.els.qtyPopover) {
        row.els.qtyPopover.hidden = true;
        row.els.qtyBadge.hidden = false;
      }
    }

    closeAllQtyPopovers() {
      this.rows.forEach((row) => this.closeQtyPopover(row));
    }

    setQty(row, qty) {
      row.qty = Math.min(MAX_QTY, Math.max(1, qty));
      if (row.els.qtyNum) row.els.qtyNum.textContent = String(row.qty);
      if (row.els.qtyBadgeValue) row.els.qtyBadgeValue.textContent = String(row.qty);
      if (row.els.qtyDec) row.els.qtyDec.disabled = row.qty <= 1;
      if (row.els.qtyInc) row.els.qtyInc.disabled = row.qty >= MAX_QTY;
      this.updateRowPrice(row);
      this.updateSummary();
    }

    /* ---------- 算价 ---------- */

    updateRowPrice(row) {
      const sale = row.variant.price * row.qty;
      const compare = (row.variant.compare > row.variant.price ? row.variant.compare : 0) * row.qty;

      if (row.els.priceCurrent) row.els.priceCurrent.innerHTML = this.formatMoney(sale);
      if (row.els.priceCompare) {
        if (compare > 0) {
          row.els.priceCompare.innerHTML = this.formatMoney(compare);
          row.els.priceCompare.hidden = false;
        } else {
          row.els.priceCompare.hidden = true;
        }
      }
    }

    calculatePricing() {
      let saleSum = 0;
      let compareSum = 0;
      let count = 0;
      let distinctChecked = 0;

      this.rows.forEach((row) => {
        if (!row.checked) return;
        const sale = row.variant.price * row.qty;
        const compare = (row.variant.compare > row.variant.price ? row.variant.compare : row.variant.price) * row.qty;
        saleSum += sale;
        compareSum += compare;
        count += row.qty;
        distinctChecked += 1;
      });

      const discountActive = distinctChecked >= 2;
      const discount = discountActive ? Math.round((saleSum * this.config.discountPercentage) / 100) : 0;
      const total = saleSum - discount;
      const savePct = compareSum > 0 ? Math.round(((compareSum - total) / compareSum) * 100) : 0;

      return { saleSum, compareSum, count, distinctChecked, discountActive, discount, total, savePct };
    }

    updateSummary() {
      const p = this.calculatePricing();
      const els = this.summaryEls;

      if (els.count) els.count.textContent = String(p.count);
      if (els.countLabel) {
        els.countLabel.textContent = p.count === 1 ? this.config.texts.item : this.config.texts.items;
      }
      if (els.subtotal) els.subtotal.innerHTML = this.formatMoney(p.saleSum);
      if (els.subtotalCompare) {
        if (p.compareSum > p.saleSum) {
          els.subtotalCompare.innerHTML = this.formatMoney(p.compareSum);
          els.subtotalCompare.hidden = false;
        } else {
          els.subtotalCompare.hidden = true;
        }
      }

      if (els.discountRow) els.discountRow.hidden = !p.discountActive;
      if (els.discountHint) els.discountHint.hidden = p.discountActive;
      if (els.discountAmount) els.discountAmount.innerHTML = this.formatMoney(p.discount);

      if (els.saveBadge) {
        if (p.savePct > 0) {
          els.saveBadge.textContent = this.config.texts.saveBadge.replace('[[p]]', String(p.savePct));
          els.saveBadge.hidden = false;
        } else {
          els.saveBadge.hidden = true;
        }
      }
      if (els.total) els.total.innerHTML = this.formatMoney(p.total);
      if (els.compareTotal) {
        if (p.compareSum > p.total) {
          els.compareTotal.innerHTML = this.formatMoney(p.compareSum);
          els.compareTotal.hidden = false;
        } else {
          els.compareTotal.hidden = true;
        }
      }
      if (els.addToCart) els.addToCart.disabled = p.count === 0;
    }

    /* ---------- 加购（沿用 bundle_builder 时序） ---------- */

    async handleAddToCart() {
      const button = this.summaryEls.addToCart;
      const originalText = button.innerHTML;
      button.disabled = true;
      button.innerHTML = `<span class="wb-cta__text">${this.escapeHtml(this.config.texts.adding)}</span>`;

      try {
        const items = this.rows
          .filter((row) => row.checked)
          .map((row) => ({ id: row.variant.id, quantity: row.qty }));

        if (!items.length) throw new Error('No products selected');

        const startTime = Date.now();
        let cartOpened = false;

        for (const item of items) {
          const response = await fetch(this.getCartEndpoint('cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items: [item] })
          });

          if (!response.ok) {
            let errorMsg = this.config.texts.addFailed;
            try {
              const errorData = await response.json();
              errorMsg = errorData.description || errorMsg;
            } catch (e) { /* noop */ }
            throw new Error(errorMsg);
          }

          if (!cartOpened) {
            const elapsed = Date.now() - startTime;
            if (elapsed < 300) {
              await new Promise((resolve) => setTimeout(resolve, 300 - elapsed));
            }

            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            const cartDrawer = document.getElementById('Cart-Drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }

            button.disabled = false;
            button.innerHTML = originalText;
            cartOpened = true;
          }

          await new Promise((resolve) => setTimeout(resolve, 150));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        console.log('[Winner Bundle] ' + this.config.texts.added);
      } catch (error) {
        console.error('[Winner Bundle] ' + this.config.texts.addFailed, error);
        button.disabled = false;
        button.innerHTML = originalText;
      }
    }

    /* ---------- 工具 ---------- */

    getRouteRoot() {
      const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      const themeRoot = window.theme && window.theme.routes && window.theme.routes.root_url;
      const root = shopifyRoot || themeRoot || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    getCartEndpoint(path) {
      return this.getRouteRoot() + path.replace(/^\//, '');
    }

    isCadMoneyContext() {
      const currencyCode = String(this.config.currencyCode || '').toUpperCase();
      const countryCode = String(this.config.countryCode || '').toUpperCase();
      const path = window.location.pathname.toLowerCase();

      return currencyCode === 'CAD' ||
        countryCode === 'CA' ||
        path.includes('/en-ca') ||
        path.includes('/fr-ca');
    }

    /**
     * 格式化金额（入参：分）。返回带 transcy-money class 的 HTML，兼容货币转换应用。
     */
    formatMoney(cents) {
      const amount = cents / 100;
      const moneyFormat = this.config.moneyFormat;
      const formattedAmount = amount.toFixed(2);
      let priceText = '';

      if (typeof formatMoney === 'function' && moneyFormat) {
        priceText = formatMoney(cents, moneyFormat);
      }

      if (!priceText && moneyFormat.includes('{{amount}}')) {
        priceText = moneyFormat.replace('{{amount}}', formattedAmount);
      } else if (!priceText && moneyFormat.includes('{{amount_with_comma_separator}}')) {
        const parts = formattedAmount.split('.');
        const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        priceText = moneyFormat.replace('{{amount_with_comma_separator}}', intPart + ',' + parts[1]);
      } else if (!priceText && moneyFormat.includes('{{amount_no_decimals}}')) {
        priceText = moneyFormat.replace('{{amount_no_decimals}}', Math.round(amount).toString());
      } else if (!priceText) {
        priceText = formattedAmount;
      }

      if (this.isCadMoneyContext() && !/\bCAD\b/.test(priceText)) {
        priceText = `${priceText} CAD`;
      }

      return `<span class="transcy-money">${priceText}</span>`;
    }

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('winner-bundle', WinnerBundle);
})();
